import React from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import { getWeatherConditionFieldLabels } from 'states/resources/weather-conditions/selectors';
/** import single application imports in the section above, Sort Lines Ascending */

export const WEATHER_FIELDS_TABLE_CELL_STYLE = { verticalAlign: 'top', paddingRight: 24 };

/**
 * Condition value. Word-highlighted history needs a bare inner div.
 * The Weather Conditions field leaves this off so its markup stays a body2 paragraph.
 * valueStyle stays on this Typography. The highlight div stays attribute-free.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.leafValues]
 * @param {Object} [props.valueStyle] - Wrap style for the value text, not the header
 */
function FieldValue({ children, leafValues, valueStyle }) {
    if (leafValues) {
        return (
            <Typography variant="body2" component="div" style={valueStyle}>
                <div>{children}</div>
            </Typography>
        );
    }

    return (
        <Typography variant="body2" style={valueStyle}>
            {children}
        </Typography>
    );
}

/**
 * Table cells for weather fields in ad history (static markup via renderToStaticMarkup).
 *
 * @param {Object} props
 * @param {Object} props.condition - Weather condition row data
 * @param {Object} props.weatherConditionsData - Bound selector fns
 * @param {Object} [props.cellStyle] - Optional td style override
 * @param {boolean} [props.leafValues] - Render values as bare divs for word highlight
 * @param {Object} [props.valueStyle] - Value-text wrap style. Callers that pass cellStyle omit this.
 */
export const WeatherConditionFieldsTableCells = ({
    condition,
    weatherConditionsData,
    cellStyle = WEATHER_FIELDS_TABLE_CELL_STYLE,
    leafValues = false,
    valueStyle,
}) => {
    const {
        conditionLabel,
        qualifierLabel,
        valueLabel,
        timeframeLabel,
    } = getWeatherConditionFieldLabels(condition, weatherConditionsData);

    return (
        <React.Fragment>
            <td style={cellStyle}>
                <Typography variant="subtitle2">Condition</Typography>
                <FieldValue leafValues={leafValues} valueStyle={valueStyle}>
                    {conditionLabel}
                </FieldValue>
            </td>
            <td style={cellStyle}>
                <Typography variant="subtitle2">Qualifier</Typography>
                <FieldValue leafValues={leafValues} valueStyle={valueStyle}>
                    {qualifierLabel}
                </FieldValue>
            </td>
            <td style={cellStyle}>
                <Typography variant="subtitle2">Value</Typography>
                <FieldValue leafValues={leafValues} valueStyle={valueStyle}>
                    {valueLabel}
                </FieldValue>
            </td>
            <td style={cellStyle}>
                <Typography variant="subtitle2">Time Frame</Typography>
                <FieldValue leafValues={leafValues} valueStyle={valueStyle}>
                    {timeframeLabel}
                </FieldValue>
            </td>
        </React.Fragment>
    );
};

/**
 * Single table row with weather field columns for ad history.
 *
 * @param {Object} props
 * @param {Object} props.condition - Weather condition row data
 * @param {Object} props.weatherConditionsData - Bound selector fns
 * @param {Object} [props.cellStyle] - Optional td style override
 * @param {Object} [props.valueStyle] - Value-text wrap style passed through to the cells
 */
export const WeatherConditionFieldsTableRow = ({
    condition,
    weatherConditionsData,
    cellStyle,
    valueStyle,
}) => (
    <tr>
        <WeatherConditionFieldsTableCells
            condition={condition}
            weatherConditionsData={weatherConditionsData}
            cellStyle={cellStyle}
            valueStyle={valueStyle}
        />
    </tr>
);
